import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MuiTooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FolderIcon from '@mui/icons-material/Folder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ReorderableBlockList } from '../../../CommonComponents/ReorderableBlockList';
import {
  IPublicationBrandings,
  IPublicationNavMenuStory
} from '@opengov/stories-shared/types';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { IOnlinePublicationMessages, IStoriesFolder, IStoryListSelection } from '../../../../../types';
import { ConfirmationModal } from '../../../../common/components/ConfirmationModal';
import { navigateToSetupOnlinePublication } from '../../../../state/navigation';
import { EmptyView } from '../../../CommonComponents/EmptyView/EmptyView';
import { InlineEditText } from '../../../CommonComponents/InlineEditText/InlineEditText';
import { useSetupOnlinePublicationUi } from '../context/SetupOnlinePublicationUi.context';
import { useDeleteNavigationMenuStory } from '../hooks/useDeleteNavigationMenuStory';
import { useOrganizeMenuStoriesList } from '../hooks/useOrganizeMenuStoriesList';
import { useUpdateMenuTitle } from '../hooks/useUpdateMenuTitle';
import { AddMenuModal } from '../PublicationModals/AddMenu';
import { ChangeDisplayNameModal } from '../PublicationModals/ChangeDisplayName/ChangeDisplayNameModal';
import { SelectStoryModal } from '../PublicationModals/SelectStory';

import { Sidebar } from './MenuSidebar/MenuSidebar';

import { styles } from './NavigationMenu.styles';

export type INavigationMenuProps = {
  entityId: number;
  entitySubdomain: string;
  messages: IOnlinePublicationMessages;
  loadingMenuItems: boolean;
  menuItems: IStoriesFolder[];
  menuStories: IPublicationNavMenuStory[];
  fetchingMenuStories: boolean;
  publicationId: string;
  publicationBrandings: IPublicationBrandings | null;
  selectedMenuName: string;
  userId: number;
  enableGoogleTranslateForPublications: boolean;
  deleteNavigationMenuById(props: { id: string; title: string; publicationId: string }): Promise<void>;
  insertPublicationNavigationMenu(props: {
    publicationId: string;
    title: string;
    userId: number;
    order: number;
  }): Promise<unknown>;
  navigateToOnlinePublicationMenu(entitySubdomain: string, publicationId: string, menuId: string): void;
  organizeMenuItemsByOrder(props: { orderedMenuIds: string[]; publicationId: string }): Promise<void>;
  handleUpdateBrandings(payload: Partial<IPublicationBrandings>): void;
};

export const NavigationMenu: React.FC<INavigationMenuProps> = (props) => {
  const {
    entityId,
    entitySubdomain,
    loadingMenuItems,
    menuItems,
    menuStories,
    fetchingMenuStories,
    messages,
    publicationId,
    publicationBrandings,
    selectedMenuName,
    userId,
    enableGoogleTranslateForPublications,
    deleteNavigationMenuById,
    insertPublicationNavigationMenu,
    navigateToOnlinePublicationMenu,
    organizeMenuItemsByOrder,
    handleUpdateBrandings
  } = props;

  const { state: uiState, dispatch: uiDispatch } = useSetupOnlinePublicationUi();
  const { isOrganizationMenuOrStoriesInProgress, selectedMenuId } = uiState;

  const organizeStoriesMutation = useOrganizeMenuStoriesList({
    successTitle: messages.changesSaved,
    successBody: messages.changesSavedSuccessfully,
    errorTitle: messages.somethingWentWrongError,
    errorBody: messages.changesSavedFailed
  });
  const deleteMenuStoryMutation = useDeleteNavigationMenuStory(
    { publicationId, menuId: selectedMenuId ?? '' },
    {
      successTitle: messages.changesSaved,
      successBody: messages.changesSavedSuccessfully,
      errorTitle: messages.somethingWentWrongError,
      errorBody: messages.errorDescription
    }
  );
  const updateMenuTitleMutation = useUpdateMenuTitle(
    { publicationId },
    {
      successTitle: messages.changesSaved,
      successBody: messages.changesSavedSuccessfully,
      errorTitle: messages.somethingWentWrongError,
      errorBody: messages.errorDescription
    }
  );

  const [isReorderMenuActive, setIsReorderMenuActive] = useState(false);
  const [isSelectStoryModalVisible, setIsSelectStoryModalVisible] = useState(false);
  const [isAddMenuModalVisible, setIsAddMenuModalVisible] = useState(false);
  const [isChangeDisplayNameModalVisible, setIsChangeDisplayNameModalVisible] = useState(false);
  const [selectedMenuStory, setSelectedMenuStory] = useState<IPublicationNavMenuStory>(null);

  const [reorderableMenuItems, setReorderableMenuItems] = useState(menuItems);
  const [isDeleteMenuModalVisible, setIsDeleteMenuModalVisible] = useState<boolean>(false);
  const [isDeletingMenu, setIsDeletingMenu] = useState<boolean>(false);

  const [reorderableStoriesList, setReorderableStoriesList] = useState(menuItems);
  const [isOrganizeStoriesInProgress, setIsOrganizeStoriesInProgress] = useState<boolean>(false);

  useEffect(() => {
    if (menuItems?.length) {
      const selectedMenuIdPresent = menuItems.filter((item) => item.id === selectedMenuId);
      if (!selectedMenuId || !selectedMenuIdPresent.length) {
        uiDispatch({ type: 'SET_SELECTED_MENU_ID', menuId: menuItems[0]?.id });
        navigateToOnlinePublicationMenu(entitySubdomain, publicationId, menuItems[0]?.id);
      }
      setReorderableMenuItems(menuItems);
    }
  }, [publicationId, menuItems, isReorderMenuActive]);

  useEffect(() => {
    if (menuStories?.length) {
      setReorderableStoriesList(menuStories);
    }
  }, [menuStories]);

  const renderModals = () => {
    const selectStoryModal =
      isSelectStoryModalVisible &&
      createPortal(
        <SelectStoryModal
          entityId={entityId}
          menuStories={menuStories}
          messages={messages}
          publicationId={publicationId}
          selectedMenuId={selectedMenuId ?? ''}
          selectedMenuName={selectedMenuName}
          userId={userId}
          onClose={() => setIsSelectStoryModalVisible(false)}
        />,
        document.getElementById('app')
      );

    const addMenuModal =
      isAddMenuModalVisible &&
      createPortal(
        <AddMenuModal
          menuItems={menuItems}
          messages={messages}
          insertPublicationNavigationMenu={insertPublicationNavigationMenu}
          onClose={() => setIsAddMenuModalVisible(false)}
          publicationId={publicationId}
          userId={userId}
        />,
        document.getElementById('app')
      );

    const changeDisplayNameModal =
      isChangeDisplayNameModalVisible &&
      createPortal(
        <ChangeDisplayNameModal
          menuStories={menuStories}
          messages={messages}
          onClose={() => setIsChangeDisplayNameModalVisible(false)}
          selectedMenuStory={selectedMenuStory}
        />,
        document.getElementById('app')
      );
    return [selectStoryModal, addMenuModal, changeDisplayNameModal];
  };

  const renderDeleteMenuConfirmation = () => {
    const handleClose = () => setIsDeleteMenuModalVisible(false);
    const handleConfirm = () => {
      if (!selectedMenuId) return;
      setIsDeletingMenu(true);
      void deleteNavigationMenuById({ id: selectedMenuId, title: selectedMenuName, publicationId })
        .then(() => {
          uiDispatch({ type: 'SET_UPDATE_PUBLICATION_PREVIEW', needsRefresh: true });
          handleClose();
        })
        .finally(() => {
          setIsDeletingMenu(false);
        });
    };

    return (
      <ConfirmationModal
        title={messages.deleteMenu}
        description={messages.deletMenuDesc}
        dataTest="delete-menu-confirmation-modal"
        confirmationRequired={true}
        confirmationText={messages.delete}
        confirmationButtonVariant="destructive"
        actionPending={isDeletingMenu}
        onCancel={handleClose}
        onProceed={handleConfirm}
      />
    );
  };

  const onSectionDragEnd = (sourceIndex: number, destinationIndex: number) => {
    const updatedMenuItems = [...reorderableMenuItems];
    const reorderedNode = updatedMenuItems.splice(sourceIndex, 1)[0];
    updatedMenuItems.splice(destinationIndex, 0, reorderedNode);
    uiDispatch({ type: 'SET_ORGANIZATION_IN_PROGRESS', inProgress: true });
    setReorderableMenuItems(updatedMenuItems);
  };

  const saveReorderedMenuItemsAction = () => {
    if (isReorderMenuActive) {
      const orderedMenuIds = reorderableMenuItems.map((menuItem) => menuItem.id);
      setIsReorderMenuActive(false);
      uiDispatch({ type: 'SET_ORGANIZATION_IN_PROGRESS', inProgress: false });
      void organizeMenuItemsByOrder({
        orderedMenuIds,
        publicationId
      }).then(() => {
        uiDispatch({ type: 'SET_UPDATE_PUBLICATION_PREVIEW', needsRefresh: true });
        setReorderableMenuItems(null);
      });
    } else {
      uiDispatch({ type: 'SET_ORGANIZATION_IN_PROGRESS', inProgress: true });
      const orderedStoriesIds = reorderableStoriesList.map((story) => story.id);
      void organizeStoriesMutation
        .mutateAsync({
          orderedStoriesIds,
          menuId: selectedMenuId ?? ''
        })
        .then(() => {
          uiDispatch({ type: 'SET_UPDATE_PUBLICATION_PREVIEW', needsRefresh: true });
          uiDispatch({ type: 'SET_ORGANIZATION_IN_PROGRESS', inProgress: false });
          setReorderableStoriesList(null);
          setIsOrganizeStoriesInProgress(false);
        })
        .catch(() => {
          uiDispatch({ type: 'SET_ORGANIZATION_IN_PROGRESS', inProgress: false });
          setIsOrganizeStoriesInProgress(false);
        });
    }
  };

  const emptyViewButtonAction = () => {
    if (!menuItems?.length) {
      setIsAddMenuModalVisible(true);
    } else {
      setIsSelectStoryModalVisible(true);
    }
  };

  const setSelectedMenuItem = (menuItem: IStoryListSelection) => {
    if ((!menuItem && !selectedMenuId) || menuItem?.id === selectedMenuId) return;
    if (menuItem) {
      uiDispatch({ type: 'SET_SELECTED_MENU_ID', menuId: menuItem.id });
      navigateToOnlinePublicationMenu(entitySubdomain, publicationId, menuItem.id);
    } else {
      uiDispatch({ type: 'SET_SELECTED_MENU_ID', menuId: null });
      navigateToSetupOnlinePublication(entitySubdomain, publicationId);
    }
  };

  const onStoriesDragEnd = (sourceIndex: number, destinationIndex: number) => {
    const updatedStoriesList = [...reorderableStoriesList];
    const reorderedNode = updatedStoriesList.splice(sourceIndex, 1)[0];
    updatedStoriesList.splice(destinationIndex, 0, reorderedNode);
    uiDispatch({ type: 'SET_ORGANIZATION_IN_PROGRESS', inProgress: true });
    setIsOrganizeStoriesInProgress(true);
    setReorderableStoriesList(updatedStoriesList);
  };

  const renderStoriesList = () => {
    const menuHasSingleStory = menuStories.length === 1;
    return (
      <ReorderableBlockList dataTest="reorderable-stories-list" onDragEnd={onStoriesDragEnd}>
        {(reorderableStoriesList || []).map((menuStory: IPublicationNavMenuStory) => (
          <ReorderableBlockList.Item
            dataTest="reorderable-stories-list-item"
            key={menuStory.id}
            id={menuStory.id}
            label={menuStory.displayName}
          >
            <Box sx={styles.storiesItemContainer}>
              <Box sx={[styles.storiesItemLabel, menuHasSingleStory && styles.flexRow]}>
                <Typography variant="body2" sx={styles.organizeStoriesDescText}>{menuStory?.story?.title}</Typography>
                {menuStory.displayName && !menuHasSingleStory && (
                  <Typography variant="body2" sx={styles.organizeStoriesDescText}>
                    {`${messages.displayName}: ${menuStory.displayName}`}
                  </Typography>
                )}
                {menuHasSingleStory && (
                  <MuiTooltip
                    title={messages.singleStoryTooltip}
                    placement="bottom"
                  >
                    <Box sx={styles.iconContainer} data-test="icon-container">
                      <InfoOutlinedIcon aria-hidden={true} sx={styles.outlineIcon} />
                    </Box>
                  </MuiTooltip>
                )}
              </Box>
              <Box sx={styles.storiesItemButtons}>
                {!menuHasSingleStory && (
                  <IconButton
                    size="small"
                    sx={styles.storiesReorderableItemButton}
                    data-test="stories-edit-title-button"
                    onClick={() => {
                      setSelectedMenuStory(menuStory);
                      setIsChangeDisplayNameModalVisible(true);
                    }}
                    aria-label="Edit story title"
                  >
                    <EditIcon aria-hidden={true} fontSize="small" />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  sx={styles.storiesReorderableItemButton}
                  data-test="stories-delete-button"
                  onClick={() => {
                    void deleteMenuStoryMutation.mutateAsync({ id: menuStory.id }).then(() => {
                      uiDispatch({ type: 'SET_UPDATE_PUBLICATION_PREVIEW', needsRefresh: true });
                    });
                  }}
                  aria-label="Delete story"
                >
                  <DeleteIcon aria-hidden={true} fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </ReorderableBlockList.Item>
        ))}
      </ReorderableBlockList>
    );
  };

  const organizeInProgressButtons = () => (
    <Box sx={styles.buttons}>
      <Button
        variant="outlined"
        size="small"
        data-test="organize-cancel-button"
        onClick={() => {
          uiDispatch({ type: 'SET_ORGANIZATION_IN_PROGRESS', inProgress: false });
          if (isReorderMenuActive) {
            setIsReorderMenuActive(false);
          } else {
            setIsOrganizeStoriesInProgress(false);
            setReorderableStoriesList(menuStories);
          }
        }}
      >
        {messages.cancel}
      </Button>
      <Button
        variant="contained"
        size="small"
        disabled={!isOrganizationMenuOrStoriesInProgress}
        data-test="organize-save-button"
        onClick={saveReorderedMenuItemsAction}
      >
        {messages.save}
      </Button>
    </Box>
  );

  const onChangeMenuTitle = (title) => {
    if (title !== selectedMenuName && selectedMenuId) {
      void updateMenuTitleMutation.mutateAsync({ id: selectedMenuId, title }).then(() => {
        uiDispatch({ type: 'SET_UPDATE_PUBLICATION_PREVIEW', needsRefresh: true });
      });
    }
  };

  const renderEmptyView = () => {
    const showEmptyViewButton = !isReorderMenuActive && !(menuItems?.length && menuStories.length);

    const getEmptyViewButtonTitle = () => {
      if (!menuItems?.length) {
        return messages.addMenu;
      } else if (!menuStories.length) {
        return messages.addStories;
      }
      return '';
    };

    const getEmptyViewTitle = () => {
      if (!isReorderMenuActive) {
        return !menuItems?.length ? messages.emptyMenuTitle : messages.emptyStoriesTitle;
      }
      return '';
    };

    const getEmptyViewDescription = () => {
      if (isReorderMenuActive) {
        return messages.saveChangesMenuReorder;
      } else if (!menuItems?.length) {
        return messages.emptyMenuDesc;
      } else if (!menuStories.length) {
        return messages.emptyStoriesDesc;
      }
      return '';
    };

    return (
      <EmptyView
        title={getEmptyViewTitle()}
        description={getEmptyViewDescription()}
        showButton={showEmptyViewButton}
        buttonTitle={getEmptyViewButtonTitle()}
        buttonVariant="outlined"
        buttonSize="small"
        buttonAction={emptyViewButtonAction}
      />
    );
  };

  const renderRightContent = () => {
    if (!menuStories.length && fetchingMenuStories) {
      return (
        <Box sx={styles.centered} data-test="story-list-loading-spinner">
          <CircularProgress role="status" />
        </Box>
      );
    }

    return !menuItems.length ? (
      renderEmptyView()
    ) : (
      <Box sx={styles.rightContentContainer}>
        <Box sx={styles.contentHeader}>
          <InlineEditText
            buttonDisabled={isOrganizeStoriesInProgress}
            messages={messages}
            text={selectedMenuName}
            saveAction={onChangeMenuTitle}
          />
          <Box sx={styles.buttons}>
            {isOrganizeStoriesInProgress ? (
              organizeInProgressButtons()
            ) : (
              <>
                {!!menuStories?.length && (
                  <Button
                    variant="outlined"
                    size="small"
                    data-test="add-stories-button-right-content"
                    onClick={() => {
                      setIsSelectStoryModalVisible(true);
                    }}
                  >
                    {messages.addStories}
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  data-test="delete-menu-button"
                  onClick={() => setIsDeleteMenuModalVisible(true)}
                >
                  {messages.delete}
                </Button>
              </>
            )}
          </Box>
        </Box>
        <Box sx={styles.description}>
          <Typography sx={styles.organizeStoriesText}>
            {messages.onlineOrganizeStories.toUpperCase()}
          </Typography>
          <Typography variant="body2" sx={styles.organizeStoriesDescText}>{messages.onlineOrganizeStoriesDesc}</Typography>
        </Box>
        <Divider aria-hidden={true} />
        {!menuStories.length || isReorderMenuActive ? (
          renderEmptyView()
        ) : (
          <Box sx={styles.reorderableListView}>{renderStoriesList()}</Box>
        )}
      </Box>
    );
  };

  const renderGoogleTranslatorButton = () => {
    return (
      <FormControlLabel
        sx={styles.navBarCheckbox}
        data-test="nav-google-translate"
        disabled={isOrganizationMenuOrStoriesInProgress}
        label={messages.enableGoogleTranslate}
        control={
          <Checkbox
            size="small"
            checked={publicationBrandings?.enableGoogleTranslate}
            onChange={(e) => handleUpdateBrandings({ enableGoogleTranslate: e.target.checked })}
          />
        }
      />
    );
  };

  return (
    <Box sx={styles.container}>
      {renderModals()}
      {isDeleteMenuModalVisible && renderDeleteMenuConfirmation()}
      <Box sx={styles.fullWidth}>
        <Box sx={styles.navBarHeader}>
          <Typography component="p" variant="h6" sx={styles.navBarTitle}>
            {messages.navBar}
          </Typography>
          <Typography component="p" variant="body2" sx={styles.navBarDesc}>
            {messages.navBarDesc}
          </Typography>
          <FormControlLabel
            sx={styles.navBarCheckbox}
            data-test="nav-menu-checkbox"
            disabled={isOrganizationMenuOrStoriesInProgress}
            label={messages.stickyNavBarDesc}
            control={
              <Checkbox
                size="small"
                checked={publicationBrandings?.isNavigationMenuSticky}
                onChange={(e) => handleUpdateBrandings({ isNavigationMenuSticky: !!e.target.checked })}
              />
            }
          />
          {enableGoogleTranslateForPublications && renderGoogleTranslatorButton()}
        </Box>
        <Box sx={styles.content}>
          <Box sx={styles.leftContent}>
            {!isReorderMenuActive && (
              <Sidebar
                isOrganizeStoriesInProgress={isOrganizeStoriesInProgress}
                loadingMenuItems={loadingMenuItems}
                messages={messages}
                menuItems={menuItems}
                currentFolderId={selectedMenuId ?? ''}
                setIsReorderMenuActive={setIsReorderMenuActive}
                setIsAddMenuModalVisible={setIsAddMenuModalVisible}
                setSelectedMenu={setSelectedMenuItem}
              />
            )}
            {isReorderMenuActive && (
              <Box sx={styles.reorderableList}>
                <Box sx={styles.filterRootSectionHeader}>
                  <Box sx={styles.flexRow}>
                    <FolderIcon aria-hidden={true} sx={styles.folderIcon} />
                    <Typography variant="subtitle1" sx={styles.foldersHeader}>
                      {messages.menu}
                    </Typography>
                  </Box>
                  {isReorderMenuActive && organizeInProgressButtons()}
                </Box>
                <Divider aria-hidden={true} />
                <Box sx={styles.reorderableListView}>
                  <ReorderableBlockList dataTest="reorderable-menu-list" onDragEnd={onSectionDragEnd}>
                    {(reorderableMenuItems || []).map((menuItem: IStoriesFolder) => (
                      <ReorderableBlockList.Item
                        dataTest="reorderable-menu-list-item"
                        key={menuItem.id}
                        id={menuItem.id}
                        label={menuItem.displayName}
                      />
                    ))}
                  </ReorderableBlockList>
                </Box>
              </Box>
            )}
          </Box>
          <Box sx={styles.rightContent}>{renderRightContent()}</Box>
        </Box>
      </Box>
    </Box>
  );
};
